import { type AxiosError } from "axios";
import { defineMessages, useIntl } from "react-intl";

import {
  MutationActions,
  useEntityMutation,
  QueryKeys,
  queryClient,
} from "@/api/core";
import { useApi } from "@/hooks";
import { statusSchema, type Status } from "@/schemas";
import toast from "@/toast";
import { type ApiError } from "@/types/api";

import { updateStatusCache } from "../utils";

const messages = defineMessages({
  pinSuccess: {
    id: "status.pin.success",
    defaultMessage: "Pinned to Profile!",
  },
  deletedTruth: {
    id: "status.error.deletedTruth",
    defaultMessage: "This Truth has been deleted and is no longer available.",
  },
});

interface Variables {
  id: string;
}

function usePinStatus() {
  const api = useApi();
  const intl = useIntl();

  return useEntityMutation({
    globalQueryKey: QueryKeys.statuses.global,
    idAttributeFromVariables: "id",
    action: MutationActions.UPDATE,
    schema: statusSchema,
    mutationFn: (variables: Variables) =>
      api.post<Status>(`/api/v1/statuses/${variables.id}/pin`),
    onMutate(variables) {
      const [prevStatus] = updateStatusCache(variables.id, () => ({
        pinned: true,
      }));

      return { prevStatus };
    },
    onSuccess(response) {
      queryClient.invalidateQueries({
        queryKey: QueryKeys.statuses.account(response.data.account.id, {
          pinned: true,
          with_muted: true,
        }),
      });

      toast.success(intl.formatMessage(messages.pinSuccess));
    },
    onError(error: AxiosError<ApiError>, variables, context) {
      if (context) {
        queryClient.setQueryData(
          QueryKeys.statuses.detail(variables.id),
          context.prevStatus,
        );
      }

      const data = error.response?.data;

      if (error.response?.status === 404) {
        toast.error(intl.formatMessage(messages.deletedTruth));
      } else if (data?.error) {
        toast.error(data.error);
      }
    },
  });
}

export { usePinStatus };
